import React, { useState, useEffect, useMemo, useRef, useCallback } from "react";
import { Card, Col, Row } from "react-bootstrap";
import { Filter, Spinner } from "../../Common";
import {
  Subscription,
  Portfolio,
  AddBank,
  BuyStack,
  AccountTransactions,
  FollowUp,
} from "../index";
import {
  getCustomerDetails,
  getCustomerList,
  getUsersPartnerList,
  searchCustomerList,
  countryList
} from "../../api";
import PnlStatements from "../ProfitAndLoss/PnlStatements";
import TrustedContact from "./TrustedContact/TrustedContact";

import PartnerAndPlan from "./PartnerAndPlan/PartnerAndPlan";
import { format } from "date-fns";
import Detail from "./Detail";
import Documents from "./Documents";
import styles from "./Customer.module.css";
import Tab from "../../Common/Tab/Tab";
import DataTable from "../../Common/DataTable/DataTable";
import Button from "../../Common/Button";
import BulkPlanMigration from "./BulkPlanMigration";
import { useSearchParams } from "react-router-dom";
import SearchFilterBar from "../../Common/SearchFilter/SearchFilterBar";
import withFeatureActions from "../../withFeatureActions";
import { useRoleFeature } from "../../context/RoleFeatureContext";
import { CookieUtils } from "../../axios.config";
import { productType } from "../../utils/utils";
const statusmapping = {
  1: "PENDING",
  2: "APPROVED",
  3: "REJECTED",
  4: "CLOSED",
  KYC_MANUAL_REVIEW: "KYC MANUAL REVIEW",
};

const getStatusColor = (statusCode) => {
  switch (statusCode) {
    case "APPROVED":
      return "#4CAF50";
    case "PENDING":
      return "#FF9800";
    case "REJECTED":
      return "#F44336";
    case "CLOSED":
      return "#757575";
    case "MANUAL_REVIEW":
      return "#FFC107";
    default:
      return "#fff";
  }
};

const getStatusLabel = (statusCode) => {
  return statusmapping[statusCode] || statusCode;
};

const columns = [
  {
    Header: "Name",
    accessor: "full_name",
    search: false,
    sort: false,
  },
  {
    Header: "Email",
    accessor: "email_address",
    search: false,
    sort: false,
  },
  {
    Header: "Username",
    accessor: "user_name",
    search: false,
    sort: false,
  },
  {
    Header: "Account Number",
    accessor: "account_num",
    search: false,
    sort: false,
  },
  {
    Header: "Phone Number",
    accessor: "phone_num",
    search: false,
    sort: false,
  },
  {
    Header: "Partner",
    accessor: "partner_name",
    search: false,
    sort: true,
  },
  {
    Header: "Customer Id",
    accessor: "cust_id",
    search: false,
    sort: true,
  },
  {
    Header: "User Status",
    accessor: "status",
    search: false,
    sort: true,
    Cell: (props) => {
      const statusCode = props.row.original.status;
      return (
        <span
          style={{
            color: getStatusColor(statusCode),
            fontWeight: "500",
          }}
        >
          {getStatusLabel(statusCode)}
        </span>
      );
    },
  },
  {
    Header: "Created Date",
    accessor: "created_at",
    search: false,
    sort: true,
  },
];

const Customer = () => {
  const allTabs = [
    { key: "Details", component: "Details" },
    { key: "Documents", component: "Documents" },
    { key: "Subscription", component: "Subscription" },
    { key: "Portfolio", component: "Portfolio" },
    { key: "Bank", component: "Bank" },
    { key: "BuyStack", component: "Buy Stack" },
    { key: "Transactions", component: "Transactions" },
    { key: "FollowUp", component: "Follow Up" },
    { key: "P&L Statements", component: "PnL Statement" },
    { key: "Trusted Contact Person", component: "Trusted Contact Person" },

    { key: "Partner Mapping", component: "Partner Plan" },
  ];
  const { roleFeatureList } = useRoleFeature();
  const hasAccessInSource = (
    source,
    componentName,
    accessType = "readAccess"
  ) => {
    if (!roleFeatureList?.features) return false;
    const sourceFeatures = roleFeatureList.features.find(
      (featureGroup) => featureGroup.source === source
    );
    if (!sourceFeatures) return false;
    const parentFeature = sourceFeatures.featureList.find(
      (feature) => feature.componentName === componentName
    );
    if (parentFeature && parentFeature[accessType]) {
      return true;
    }
    const subFeature = sourceFeatures.featureList
      .flatMap((feature) => feature.subFeatures || [])
      .find((sub) => sub.componentName === componentName);
    return subFeature && subFeature[accessType];
  };
  const clientName = CookieUtils.get("clientName");
  const defaultPartner = clientName === "STOCKAL" ? "ALL" : clientName;
  const timeoutRef = useRef(null);
  const [showFilter, setShowFilter] = useState(false);
  const [partner, setPartner] = useState(defaultPartner);
  const [list, setList] = useState([]);
  const [key, setKey] = useState("");
  const [showDetail, setShowDetail] = useState(false);
  const [showTable, setShowTable] = useState(true);
  const [showBulkPlanMigration, setShowBulkPlanMigration] = useState(false);
  const [searchLoading, setSearchLoading] = useState(false);
  const [dataLoading, setDataLoading] = useState(false);
  const [inputText, setInputText] = useState("");
  const [custId, setCustId] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [partnerList, setPartnerList] = useState([]);
  const [searchParams, setSearchParams] = useSearchParams();
  const [nextStep, setNextStep] = useState("");
  const [accountStatus, setAccountStatus] = useState("");
  const [showForm, setShowForm] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(20);
  const [totalPages, setTotalPages] = useState(1);
  const [currentListStatus, setCurrentListStatus] = useState("");
  const [userData, setUserData] = useState({});
  const [countryOptions, setCountryOptions] = useState([]);
  const [loadingCountries, setLoadingCountries] = useState(true);
  const isLoadingListStatus = useRef(false);
  const firstRender = useRef(true);
  const filterEffectFirstRender = useRef(true);
  const initialListLoaded = useRef(false);
  const currentSearchRef = useRef("");

  const DetailWithAccess = withFeatureActions(Detail, "INVESTING", "Details");
  const TrustedContactWithAccess = withFeatureActions(TrustedContact  , "INVESTING", "Trusted Contact Person");
  const PartnerAndPlanWithAccess = withFeatureActions(PartnerAndPlan, "INVESTING", "Partner Plan");
  const DocumentsWithAccess = withFeatureActions(
    Documents,
    "INVESTING",
    "Documents"
  );
  const AddBankWithAccess = withFeatureActions(AddBank, "INVESTING", "Bank");
  const BuyStackWithAccess = withFeatureActions(
    BuyStack,
    "INVESTING",
    "Buy Stack"
  );
  const FollowUpWithAccess = withFeatureActions(
    FollowUp,
    "INVESTING",
    "Follow Up"
  );
  const [startDate, setStartDate] = useState(() => {
    const date = new Date();
    date.setTime(date.getTime() - 15 * 24 * 60 * 60 * 1000);
    return date;
  });
  const [endDate, setEndDate] = useState(new Date());

  const keyToParamMap = {
    Details: "DE",
    Documents: "VD",
    Portfolio: "PO",
    Subscription: "SU",
    Bank: "AB",
    BuyStack: "BS",
    Transactions: "AT",
    FollowUp: "FP",
    "P&L Statements": "PL",
    "Trusted Contact Person": "TCP",
    "Partner Mapping": "PP",
  };
    const availableTabs = useMemo(() => {
    return allTabs.filter((tab) => {
      const hasAccess = hasAccessInSource("INVESTING", tab.component, "readAccess");

      if (tab.key === "Trusted Contact Person" && !userData?.dwaccountNo) {
        return false;
      }

      return hasAccess;
    });
  }, [roleFeatureList, userData?.dwaccountNo]);
  const hasCustomerWriteAccess = hasAccessInSource("INVESTING", "Customers", "writeAccess");
  const keys = availableTabs.map((tab) => tab.key);
  useEffect(() => {
    if (keys.length > 0 && !key) {
      setKey(keys[0]);
    }
  }, [keys, key]);

  useEffect(() => {
    getPartnerApi();
  }, []);
    useEffect(() => {
    const fetchCountries = async () => {
      try {
        const resp = await countryList();
        if (resp?.data?.code === 200 && Array.isArray(resp.data.data)) {
          const options = resp.data.data.map((item) => ({
            label: item.countryname,
            value: item.country,
          }));
          setCountryOptions(options);
        }
      } catch (err) {
        console.error("Error fetching countries:", err);
      } finally {
        setLoadingCountries(false);
      }
    };

    fetchCountries();
  }, []);

  const getPartnerApi = async () => {
    try {
      const resp = await getUsersPartnerList({ appName: productType.INVESTING });

      if (resp?.data?.code === 200) {
        const partners = resp?.data?.data.map((item) => ({
          name: item.partnerName,
          code: item.partnerCd,
        }));
        setPartnerList([{ name: "ALL", code: "ALL" }, ...partners]);
      }
    } catch (error) {}
  };

  const partnerOptions =
    partnerList?.map((partner) => ({
      label: partner.name,
      value: partner.code,
    })) || [];

  const fetchListStatus = useCallback(async (custIdToFetch) => {
    if (!custIdToFetch || isLoadingListStatus.current) return;

    isLoadingListStatus.current = true;
    try {
      const res = await searchCustomerList(custIdToFetch, 1, 0);
      if (res?.data?.data && res.data.data.length > 0) {
        const customerData = res.data.data[0];
        setCurrentListStatus(customerData.status || "");
      }
    } catch (error) {
      console.error("Error fetching customer list status:", error);
    } finally {
      isLoadingListStatus.current = false;
    }
  }, []);

  useEffect(() => {
    const id = searchParams.get("id");
    const tabParam = searchParams.get("tab");
    if (id && tabParam) {
      setCustId(id);
      setShowDetail(true);
      setShowTable(false);
      if (!currentListStatus) {
        fetchListStatus(id);
      }
      const selectedKey = Object.keys(keyToParamMap).find(
        (k) => keyToParamMap[k] === tabParam
      );
      if (selectedKey && keys.includes(selectedKey)) {
        setKey(selectedKey);
      } else if (keys.length > 0) {
        setKey(keys[0]);
      }
    }
  }, [searchParams, keys, fetchListStatus, currentListStatus]);

  const getCustomerData = async (
    username,
    partner,
    start,
    end,
    accountStatus,
    nextStep,
    pageNumber = 1,
    pageSize = 20
  ) => {
    try {
      setSearchLoading(true);
      setDataLoading(true);
      const res = await getCustomerList({
        username,
        partnerCode: partner && partner.trim() !== "" ? partner : "ALL",
        startDate: start,
        endDate: end,
        accountStatus: accountStatus,
        nextStep: nextStep,
        LIMIT: pageSize,
        OFFSET: pageNumber - 1,
      });

      setSearchLoading(false);
      setDataLoading(false);
      if (res && res?.data?.data) {
        setList(res.data.data);
        const total = res.data.total || 0;
        setTotalPages(Math.ceil(total / pageSize));
      }
    } catch (error) {
      setSearchLoading(false);
      setDataLoading(false);
    }
  };

  const handleSearch = async (searchText, pageNumber = 1) => {
    if (searchText !== currentSearchRef.current) {
      return;
    }
    if (searchText !== "") {
      setDataLoading(true);
      try {
        const encodedSearch = encodeURIComponent(searchText.trim());
        const res = await searchCustomerList(
          encodedSearch,
          pageSize,
          pageNumber - 1
        );
        if (searchText === currentSearchRef.current) {
          setDataLoading(false);
          setList(res.data.data);
          const total = res.data.total || 0;
          setTotalPages(Math.ceil(total / pageSize));
        }
      } catch (e) {
        if (searchText === currentSearchRef.current) {
          setDataLoading(false);
        }
      }
    } else {
      if (searchText === currentSearchRef.current) {
        setList([]);
        setTotalPages(1);
      }
    }
  };

  const onSubmit = () => {
    setInputText("");

    if (!startDate || !endDate) {
      setErrorMessage("Please Select Date");
      return;
    }

    if (!partner) {
      setErrorMessage("Partner is required");
      return;
    }

    setErrorMessage("");
    const dateStart = format(startDate, "yyyy-MM-dd");
    const dateEnd = format(endDate, "yyyy-MM-dd");
    setCurrentPage(1);
    getCustomerData(
      "",
      partner,
      dateStart,
      dateEnd,
      accountStatus,
      nextStep,
      1
    );
  };

  useEffect(() => {
    if (key === "Bank") {
      setShowForm(true);
    }
  }, [key]);
  const handleClickRow = (data) => {
    if (!data?.cust_id) return;
    const defaultTab = "DE";
    const url = `/customer?id=${data.cust_id}&tab=${defaultTab}&status=${encodeURIComponent(data.status)}`;
    window.open(url, "_blank", "noopener,noreferrer");
  };

  const getDetails = useCallback(async (custId) => {
    try {
      const resp = await getCustomerDetails(custId);
      if (resp) {
        const data = resp;

        let formattedDateTime = "";
        if (data.accountCreatedDate) {
          const date = new Date(data.accountCreatedDate);
          if (!isNaN(date)) {
            const formattedDate = date
              .toLocaleDateString("en-GB", {
                year: "numeric",
                month: "2-digit",
                day: "2-digit",
              })
              .replace(/\//g, "-");
            const formattedTime = date.toLocaleTimeString("en-US", {
              hour: "2-digit",
              minute: "2-digit",
              hour12: true,
            });

            formattedDateTime = `${formattedDate}, ${formattedTime}`;
          }
        }

        const obj = {
          fullName: data?.fullName || "",
          email: data?.email || "",
          phno: data?.phoneNumber || "",
          userName: data?.userName || "",
          accountStatus: data?.accountStatus || "",
          accountcreatedAt: formattedDateTime,
          partnerCode: data?.partnerCode || "",
          customerId: data?.customerId || "",
          refCode: data?.refCode || "",
          dob: data?.DOB || "",
          politicallyExposedNames: data?.politicallyExposedNames || "",
          gender: data?.gender || "",
          country: data?.country || "",
          state: data?.state || "",
          city: data?.city || "",
          postalCode: data?.postalCode || "",
          address1: data?.address1 || "",
          address2: data?.address2 || "",
          empStatus: data?.empStatus || "",
          empcomp: data?.company || "",
          business: data?.business || "",
          isBroker: data?.isBroker ?? "",
          directorOf: data?.directorOf || "",
          investmentObjectives: data?.investmentObjectives || "",
          investmentExperience: data?.investmentExperience || "",
          annual: data?.annual || "",
          netWorth: data?.netWorth || "",
          totalNetWorth: data?.totalNetWorth || "",
          riskTolerance: data?.riskTolerance || "",
          usTaxPayer: String(data?.usTaxPayer ?? false),
          dwuserId: data?.dwUserID || "",
          dwaccountNo: data?.dwAccountNo || "",
          dwaccountId: data?.dwaccountId || "",
          fund: data?.fund || "",
          firstName: data?.firstName || "",
          lastName: data?.lastName || "",
          middleName: data?.middleName || "",
          extendedHour: String(data?.extendedHour ?? false),
          securityLanding: data?.securityLanding || "",
          kycStatus: data?.kycStatus || "",
          virtualAccount: data?.virtualAccount || "",
          nextStep: data?.nextStep || "",
          govtIdType: data?.govtIdType || "",
          govtIdNo: data?.govtIdNo || "",
          citizenship: data?.citizenship || "",
          trustedContact: data?.trustedContact || null,
        };

        setUserData({ ...obj });
      }
    } catch (error) { }
  }, []);

  useEffect(() => {
    if (custId !== "") {
      getDetails(custId);
    }
  }, [custId]);

  const handleInputChange = (e) => {
    setInputText(e.target.value);
  };

  const memoizedUserData = useMemo(() => ({ ...userData }), [userData]);

  const refreshListStatus = useCallback(async (custIdToRefresh) => {
    if (!custIdToRefresh || isLoadingListStatus.current) return;

    isLoadingListStatus.current = true;
    try {
      const res = await searchCustomerList(custIdToRefresh, 1, 0);
      if (res?.data?.data && res.data.data.length > 0) {
        const updatedCustomer = res.data.data[0];
        if (updatedCustomer) {
          setCurrentListStatus(updatedCustomer.status || "");
        }
      }
    } catch (error) {
      console.error("Error refreshing customer status:", error);
    } finally {
      isLoadingListStatus.current = false;
    }
  }, []);

  useEffect(() => {
    const id = searchParams.get("id");
    const dateStart = startDate ? format(startDate, "yyyy-MM-dd") : "";
    const dateEnd = endDate ? format(endDate, "yyyy-MM-dd") : "";

    if (firstRender.current) {
      firstRender.current = false;
      if (!id && inputText === "" && !initialListLoaded.current) {
        initialListLoaded.current = true;
        getCustomerData("", defaultPartner, dateStart, dateEnd, accountStatus, nextStep, currentPage);
      }
      return;
    }
    if (inputText.trim() !== "") {
      currentSearchRef.current = inputText;
      clearTimeout(timeoutRef.current);
      timeoutRef.current = setTimeout(() => {
        handleSearch(currentSearchRef.current, currentPage);
      }, 500);
    } else {
      currentSearchRef.current = "";
      if (showTable && !id) {

        const dateStart = format(startDate, "yyyy-MM-dd");
        const dateEnd = format(endDate, "yyyy-MM-dd");
        getCustomerData("", partner, dateStart, dateEnd, accountStatus, nextStep, currentPage);
      }
    }
  }, [inputText, currentPage, showTable, searchParams]);

  useEffect(() => {
    if (filterEffectFirstRender.current) {
      filterEffectFirstRender.current = false;
      return;
    }

    if (!showFilter) {
      const newStart = new Date();
      newStart.setTime(newStart.getTime() - 15 * 24 * 60 * 60 * 1000);
      const newEnd = new Date();

      setPartner(defaultPartner);
      setNextStep("");
      setAccountStatus("");
      setStartDate(newStart);
      setEndDate(newEnd);
      const dateStart = format(newStart, "yyyy-MM-dd");
      const dateEnd = format(newEnd, "yyyy-MM-dd");

      getCustomerData("", defaultPartner, dateStart, dateEnd, "", "", 1);
    }
  }, [showFilter]);

  return (
    <div className={styles.container}>
      {showTable ? (
        <>
          <Row className={styles.filterRow}>
            <Col md={10}>
              <SearchFilterBar
                inputText={inputText}
                handleInputChange={handleInputChange}
                setShowFilter={setShowFilter}
                showFilter={showFilter}
                setInputText={setInputText}
                placeholder="Search by email, phone no, userId"
              />
            </Col>
            <Col md={2}>
              {hasCustomerWriteAccess && (
                <Button
                  onClick={() => setShowBulkPlanMigration(true)}
                  title={"Bulk Plan Migration"}
                />
              )}
            </Col>
          </Row>
          {showFilter && (
            <Card className={styles.customCard}>
              <Card.Body className={styles.customCardBody}>
                <Filter
                  partner={partner}
                  setPartner={setPartner}
                  startDate={startDate}
                  endDate={endDate}
                  setStartDate={setStartDate}
                  setEndDate={setEndDate}
                  onSearch={onSubmit}
                  loading={searchLoading}
                  errorMessage={errorMessage}
                  setErrorMessage={setErrorMessage}
                  partnerList={partnerOptions}
                  nextStep={nextStep}
                  setNextStep={setNextStep}
                  accountStatus={accountStatus}
                  setAccountStatus={setAccountStatus}
                  showNextStep={true}
                  showAccountStatus={true}
                />
              </Card.Body>
            </Card>
          )}
          {dataLoading ? (
            <Spinner />
          ) : (
            <>
              {list && list.length > 0 ? (
                <>
                  <DataTable
                    title="Customer"
                    columns={columns}
                    list={list}
                    handleClickRow={handleClickRow}
                    rowClick={true}
                    totalPages={totalPages}
                    currentPage={currentPage}
                    setCurrentPage={setCurrentPage}
                    pagination={true}
                  />
                </>
              ) : (
                <div className={styles.noData}>No Data Found</div>
              )}
            </>
          )}
        </>
      ) : (
        ""
      )}
      {showDetail ? (
        <>
          {keys.length > 0 && (
            <Tab
              keys={keys}
              activeKey={key}
              onSelect={(selectedKey) => {
                setKey(selectedKey);

                const tabCode = keyToParamMap[selectedKey];
                setSearchParams(
                  { id: custId, tab: tabCode },
                  { replace: true }
                );
              }}
            />
          )}
          {key === "Details" &&
            hasAccessInSource("INVESTING", "Details", "readAccess") && (
              <DetailWithAccess
                userData={userData}
                getDetails={getDetails}
                custId={custId}
                listStatus={currentListStatus}
                refreshListStatus={refreshListStatus}
                countryOptions={countryOptions}
                loadingCountries={loadingCountries}
              />
            )}
          {key === "Documents" &&
            hasAccessInSource("INVESTING", "Documents", "readAccess") && (
              <DocumentsWithAccess
                custId={custId}
                userData={memoizedUserData}
                getDetails={getDetails}
              />
            )}
          {key === "Subscription" &&
            hasAccessInSource("INVESTING", "Subscription", "readAccess") && (
              <Subscription custId={custId} />
            )}
          {key === "Portfolio" &&
            hasAccessInSource("INVESTING", "Portfolio", "readAccess") && (
              <Portfolio custId={custId} />
            )}
          {key === "Bank" &&
            hasAccessInSource("INVESTING", "Bank", "readAccess") && (
              <AddBankWithAccess
                custId={custId}
                userData={memoizedUserData}
                showForm={showForm}
                setShowForm={setShowForm}
              />
            )}
          {key === "BuyStack" &&
            hasAccessInSource("INVESTING", "Buy Stack", "readAccess") && (
              <BuyStackWithAccess custId={custId} userData={memoizedUserData} />
            )}
          {key === "Transactions" &&
            hasAccessInSource("INVESTING", "Transactions", "readAccess") && (
              <AccountTransactions custId={custId} />
            )}
          {key === "FollowUp" &&
            hasAccessInSource("INVESTING", "Follow Up", "readAccess") && (
              <FollowUpWithAccess custId={custId} />
            )}
          {key === "P&L Statements" &&
            hasAccessInSource("INVESTING", "PnL Statement", "readAccess") && (
              <PnlStatements custId={custId} userData={memoizedUserData} />
            )}
          {key === "Trusted Contact Person" &&
            hasAccessInSource("INVESTING", "Trusted Contact Person", "readAccess") && (
              <TrustedContactWithAccess custId={custId} userData={memoizedUserData} setUserData={setUserData} />
            )}
          {key === "Partner Mapping" &&
            hasAccessInSource("INVESTING", "Partner Plan", "readAccess") && (
              <PartnerAndPlanWithAccess
                custId={custId}
                userData={memoizedUserData}
                setKey={setKey}
                setSearchParams={setSearchParams}
              />
            )}
        </>
      ) : (
        ""
      )}
      {hasCustomerWriteAccess && (
        <BulkPlanMigration
          show={showBulkPlanMigration}
          onClose={() => setShowBulkPlanMigration(false)}
        />
      )}
    </div>
  );
};

export default Customer;
